// Low-level modal primitive. Renders a full-page overlay with a centred
// card via a portal so it's not constrained by parent overflow / stacking
// contexts. Escape key dismisses; backdrop click dismisses unless
// `dismissOnBackdrop` is false (use that for confirmations the user
// must consciously cancel).
//
// We don't run a full focus trap — caller is expected to autofocus the
// most-likely action via a ref. Polish chunk later if we need it.

import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export function Dialog({
  open,
  onClose,
  children,
  dismissOnBackdrop = true,
  ariaLabel,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  dismissOnBackdrop?: boolean;
  ariaLabel?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while the dialog is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="ds-dialog-overlay"
      role="presentation"
      onMouseDown={dismissOnBackdrop ? onClose : undefined}
    >
      <div
        className="ds-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        // Stop the backdrop click handler from firing when interacting
        // with the dialog content itself.
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function DialogHeader({ children }: { children: ReactNode }) {
  return (
    <div className="ds-dialog__header">
      <h2 className="ds-dialog__title">{children}</h2>
    </div>
  );
}

export function DialogBody({ children }: { children: ReactNode }) {
  return <div className="ds-dialog__body">{children}</div>;
}

export function DialogFooter({ children }: { children: ReactNode }) {
  return <div className="ds-dialog__footer">{children}</div>;
}
